import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/config";
import "./ZuordnenPage.css";

/* ── Hilfsfunktionen ─────────────────────────────────────────────────── */
const api = (path, opts = {}) =>
  fetch(`${API_BASE}/zuordnen/api${path}`, {
    headers: { "Content-Type": "application/json", ...opts.headers },
    ...opts,
  }).then((r) => {
    if (!r.ok) throw new Error(`API ${r.status}`);
    return r.json();
  });

const fmtSize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

const SCHEMA_COLORS = {
  ausgangsrechnung: "#4caf50",
  rechnung: "#2196f3",
  kontoauszug: "#ff9800",
  bwa: "#9c27b0",
  lohn: "#e91e63",
  versorger: "#00bcd4",
  mietvertrag: "#795548",
  sonstiges: "#607d8b",
};

/* ════════════════════════════════════════════════════════════════════════ */

function ZuordnenPage() {
  const navigate = useNavigate();

  /* ── State ──────────────────────────────────────────────────────────── */
  const [roots, setRoots] = useState(null);
  const [currentPath, setCurrentPath] = useState("");
  const [browseData, setBrowseData] = useState(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [pathHistory, setPathHistory] = useState([]);

  // Schema-Defs
  const [schemas, setSchemas] = useState(null);

  // Analyse (rekursiv)
  const [analyzeResult, setAnalyzeResult] = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("wrong_folder");
  const [checks, setChecks] = useState(["wrong_folder", "schema", "missing_invoice"]);

  // Schema-Editor / Rename-Preview
  const [editingSchema, setEditingSchema] = useState(null); // schema_id
  const [editTemplate, setEditTemplate] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Actions
  const [actionLoading, setActionLoading] = useState({});
  const [actionDone, setActionDone] = useState({});
  const [selected, setSelected] = useState(new Set());

  // Ansicht-Modus
  const [viewMode, setViewMode] = useState("browse"); // "browse" | "analyze"

  // Rg-Nr Analyse Modal
  const [showRgnrModal, setShowRgnrModal] = useState(false);
  const [rgnrFormat, setRgnrFormat] = useState("standard"); // "standard" | "custom"
  const [rgnrCustomPattern, setRgnrCustomPattern] = useState("");
  const [rgnrSearchText, setRgnrSearchText] = useState("");
  const [rgnrRecursive, setRgnrRecursive] = useState(true);
  const [rgnrLoading, setRgnrLoading] = useState(false);
  const [rgnrResult, setRgnrResult] = useState(null);
  const [rgnrExpandedGroup, setRgnrExpandedGroup] = useState(null);

  // Analyse-Auswahl-Menü
  const [showAnalyseMenu, setShowAnalyseMenu] = useState(false);

  // Schema-Analyse
  const [showSchemaModal, setShowSchemaModal] = useState(false);
  const [schemaRecursive, setSchemaRecursive] = useState(true);
  const [schemaChecks, setSchemaChecks] = useState(["no_prefix", "unknown_prefix", "no_absender", "hash_in_part", "no_date", "no_schema_part", "wrong_separator"]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaResult, setSchemaResult] = useState(null);
  const [schemaExpandedFile, setSchemaExpandedFile] = useState(null);

  // Falsch geordnet
  const [showFalschModal, setShowFalschModal] = useState(false);
  const [falschRecursive, setFalschRecursive] = useState(true);
  const [falschCheckYear, setFalschCheckYear] = useState(true);
  const [falschCheckPrefix, setFalschCheckPrefix] = useState(true);
  const [falschLoading, setFalschLoading] = useState(false);
  const [falschResult, setFalschResult] = useState(null);

  // Sonstige Fehler
  const [showSonstigeModal, setShowSonstigeModal] = useState(false);
  const [sonstigeRecursive, setSonstigeRecursive] = useState(true);
  const [sonstigeLoading, setSonstigeLoading] = useState(false);
  const [sonstigeResult, setSonstigeResult] = useState(null);

  /* ── Init ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    api("/roots").then(setRoots).catch(console.error);
    api("/schemas").then((d) => setSchemas(d.schemas)).catch(console.error);
  }, []);

  /* ── Browse (mit Detail/Klassifikation) ─────────────────────────────── */
  const browseTo = useCallback(async (path) => {
    setBrowseLoading(true);
    setAnalyzeResult(null);
    setPreviewData(null);
    setEditingSchema(null);
    setActionDone({});
    setSelected(new Set());
    try {
      const data = await api(`/browse-detail?path=${encodeURIComponent(path)}`);
      setBrowseData(data);
      setCurrentPath(path);
      setViewMode("browse");
    } catch (e) {
      console.error("Browse error:", e);
    } finally {
      setBrowseLoading(false);
    }
  }, []);

  const openFolder = (folderPath) => {
    setPathHistory((h) => [...h, currentPath]);
    browseTo(folderPath);
  };

  const goUp = () => {
    if (pathHistory.length > 0) {
      const prev = pathHistory[pathHistory.length - 1];
      setPathHistory((h) => h.slice(0, -1));
      browseTo(prev);
    }
  };

  const selectRoot = (key) => {
    if (!roots) return;
    setPathHistory([]);
    browseTo(roots[key]);
  };

  /* Auto-Select root from ?root= query param once roots are loaded */
  useEffect(() => {
    if (!roots) return;
    try {
      const rootParam = new URLSearchParams(window.location.search).get("root");
      if (rootParam && roots[rootParam]) {
        setPathHistory([]);
        browseTo(roots[rootParam]);
      }
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots]);

  /* ── Rg-Nr Analyse ───────────────────────────────────────────────────── */
  const runRgnrAnalyze = async () => {
    if (!currentPath) return;
    setRgnrLoading(true);
    setRgnrResult(null);
    try {
      const data = await api("/analyze-rgnr", {
        method: "POST",
        body: JSON.stringify({
          path: currentPath,
          recursive: rgnrRecursive,
          format_pattern: rgnrFormat === "custom" ? rgnrCustomPattern : "",
          search_text: rgnrSearchText,
          check_gaps: true,
        }),
      });
      setRgnrResult(data);
      setShowRgnrModal(false);
      setViewMode("rgnr");
    } catch (e) {
      console.error("Rgnr Analyze error:", e);
      alert(`Fehler: ${e.message}`);
    } finally {
      setRgnrLoading(false);
    }
  };

  /* ── Schema-Analyse ────────────────────────────────────────────────── */
  const runSchemaAnalyze = async () => {
    if (!currentPath) return;
    setSchemaLoading(true);
    setSchemaResult(null);
    try {
      const data = await api("/analyze-schema", {
        method: "POST",
        body: JSON.stringify({ path: currentPath, recursive: schemaRecursive, checks: schemaChecks }),
      });
      setSchemaResult(data);
      setShowSchemaModal(false);
      setViewMode("schema");
    } catch (e) {
      console.error(e);
      alert(`Fehler: ${e.message}`);
    } finally {
      setSchemaLoading(false);
    }
  };

  const toggleSchemaCheck = (c) =>
    setSchemaChecks((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);

  /* ── Falsch-geordnet-Analyse ────────────────────────────────────────── */
  const runFalschAnalyze = async () => {
    if (!currentPath) return;
    setFalschLoading(true);
    setFalschResult(null);
    const activeChecks = [];
    if (falschCheckPrefix) activeChecks.push("wrong_folder");
    if (falschCheckYear) activeChecks.push("wrong_year");
    if (activeChecks.length === 0) return;
    try {
      const data = await api("/analyze", {
        method: "POST",
        body: JSON.stringify({ path: currentPath, checks: activeChecks }),
      });
      setFalschResult(data);
      setShowFalschModal(false);
      setViewMode("falsch");
    } catch (e) {
      console.error(e);
      alert(`Fehler: ${e.message}`);
    } finally {
      setFalschLoading(false);
    }
  };

  /* ── Sonstige-Fehler-Analyse ────────────────────────────────────────── */
  const runSonstigeAnalyze = async () => {
    if (!currentPath) return;
    setSonstigeLoading(true);
    setSonstigeResult(null);
    try {
      const data = await api("/analyze-misc", {
        method: "POST",
        body: JSON.stringify({ path: currentPath, recursive: sonstigeRecursive }),
      });
      setSonstigeResult(data);
      setShowSonstigeModal(false);
      setViewMode("sonstige");
    } catch (e) {
      console.error(e);
      alert(`Fehler: ${e.message}`);
    } finally {
      setSonstigeLoading(false);
    }
  };

  /* ── Analyse (rekursiv) ─────────────────────────────────────────────── */
  const runAnalyze = async () => {
    if (!currentPath) return;
    setAnalyzeLoading(true);
    setAnalyzeResult(null);
    setPreviewData(null);
    setEditingSchema(null);
    setSelected(new Set());
    setActionDone({});
    try {
      const data = await api("/analyze", {
        method: "POST",
        body: JSON.stringify({ path: currentPath, checks }),
      });
      setAnalyzeResult(data);
      setViewMode("analyze");
      if (data.wrong_folder_count > 0) setActiveTab("wrong_folder");
      else if (data.schema_errors_count > 0) setActiveTab("schema");
      else if (data.missing_invoices_count > 0) setActiveTab("missing_invoice");
    } catch (e) {
      console.error("Analyze error:", e);
    } finally {
      setAnalyzeLoading(false);
    }
  };

  /* ── Schema-Editor: Vorschau generieren ─────────────────────────────── */
  const openSchemaEditor = (schemaId) => {
    if (!schemas) return;
    const def = schemas[schemaId];
    if (!def) return;
    setEditingSchema(schemaId);
    setEditTemplate(def.template);
    setPreviewData(null);
  };

  const generatePreview = async () => {
    if (!editingSchema || !currentPath) return;
    setPreviewLoading(true);
    try {
      const data = await api("/preview-rename", {
        method: "POST",
        body: JSON.stringify({
          path: currentPath,
          schema_id: editingSchema,
          new_template: editTemplate,
        }),
      });
      setPreviewData(data);
      setActionDone({});
    } catch (e) {
      console.error("Preview error:", e);
    } finally {
      setPreviewLoading(false);
    }
  };

  /* ── Einzelaktion: Rename ───────────────────────────────────────────── */
  const doRename = async (oldPath, newName, idx) => {
    const key = `rename-${idx}`;
    setActionLoading((p) => ({ ...p, [key]: true }));
    try {
      await api("/rename", {
        method: "POST",
        body: JSON.stringify({ path: oldPath, new_name: newName }),
      });
      setActionDone((p) => ({ ...p, [key]: true }));
    } catch (e) {
      alert(`Fehler: ${e.message}`);
    } finally {
      setActionLoading((p) => ({ ...p, [key]: false }));
    }
  };

  /* ── Fix anwenden (Format-Korrektur) ────────────────────────────────── */
  const applyFix = async (filePath, newName, idx) => {
    const key = `fix-${idx}`;
    setActionLoading((p) => ({ ...p, [key]: true }));
    try {
      await api("/apply-fix", {
        method: "POST",
        body: JSON.stringify({ path: filePath, new_name: newName }),
      });
      setActionDone((p) => ({ ...p, [key]: true }));
      // Update lokale Daten
      setBrowseData((prev) => {
        if (!prev) return prev;
        const files = prev.files.map((f) =>
          f.path === filePath ? { ...f, name: newName, fixes: [], path: f.path.replace(/\/[^/]+$/, "/" + newName) } : f
        );
        return { ...prev, files, total_fixes: files.filter((f) => f.fixes?.length).length };
      });
    } catch (e) {
      alert(`Fix fehlgeschlagen: ${e.message}`);
    } finally {
      setActionLoading((p) => ({ ...p, [key]: false }));
    }
  };

  const applyAllFixes = async () => {
    if (!browseData) return;
    const fixable = browseData.files.filter((f) => f.fixes?.length > 0);
    if (fixable.length === 0) return;
    if (!window.confirm(`${fixable.length} Dateien korrigieren?`)) return;

    const fixes = fixable.map((f) => ({
      path: f.path,
      new_name: f.fixes[0].fix,
    }));

    try {
      const res = await api("/apply-fix-batch", {
        method: "POST",
        body: JSON.stringify({ fixes }),
      });
      alert(`✓ ${res.success} korrigiert, ${res.failed} fehlgeschlagen`);
      browseTo(currentPath); // neu laden
    } catch (e) {
      alert(`Batch-Fix fehlgeschlagen: ${e.message}`);
    }
  };

  /* ── Move ────────────────────────────────────────────────────────────── */
  const moveFile = async (fromPath, toPath, idx) => {
    const key = `move-${idx}`;
    setActionLoading((p) => ({ ...p, [key]: true }));
    try {
      await api("/move", {
        method: "POST",
        body: JSON.stringify({ from_path: fromPath, to_path: toPath }),
      });
      setActionDone((p) => ({ ...p, [key]: true }));
    } catch (e) {
      alert(`Fehler: ${e.message}`);
    } finally {
      setActionLoading((p) => ({ ...p, [key]: false }));
    }
  };

  /* ── Batch Rename ───────────────────────────────────────────────────── */
  const batchRenameSelected = async () => {
    if (!previewData || selected.size === 0) return;
    const actions = previewData.previews
      .filter((p) => p.changed && selected.has(p.path))
      .map((p) => ({ action: "rename", from_path: p.path, new_name: p.new_name }));
    if (actions.length === 0) return;
    if (!window.confirm(`${actions.length} Dateien umbenennen?`)) return;

    setActionLoading((p) => ({ ...p, batch: true }));
    try {
      await api("/batch", {
        method: "POST",
        body: JSON.stringify({ actions }),
      });
      // Alle als done markieren
      const done = {};
      previewData.previews.forEach((p, i) => {
        if (p.changed && selected.has(p.path)) done[`rename-${i}`] = true;
      });
      setActionDone((prev) => ({ ...prev, ...done }));
    } catch (e) {
      alert(`Batch-Fehler: ${e.message}`);
    } finally {
      setActionLoading((p) => ({ ...p, batch: false }));
    }
  };

  /* ── Helpers ────────────────────────────────────────────────────────── */
  const toggleSelect = (path) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  };
  const selectAll = (items) => setSelected(new Set(items.map((i) => i.path)));
  const selectNone = () => setSelected(new Set());
  const toggleCheck = (c) =>
    setChecks((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const buildBreadcrumbs = () => {
    if (!currentPath) return [];
    const parts = currentPath.split("/").filter(Boolean);
    return parts.map((p, i) => ({
      label: p,
      path: "/" + parts.slice(0, i + 1).join("/"),
    }));
  };

  /* ════════════════════════════════════════════════════════════════════ */
  /*  RENDER                                                             */
  /* ════════════════════════════════════════════════════════════════════ */
  return (
    <div className="zuordnen-page">
      {/* Header */}
      <div className="zuordnen-header">
        <button className="back-btn" onClick={() => navigate("/")}>← Zurück</button>
        <h2 className="page-title">Zuordnen</h2>
      </div>

      {/* Root-Auswahl */}
      {roots && !currentPath && (
        <div className="root-select">
          <h3>Bereich wählen</h3>
          <div className="root-cards">
            <button className="root-card" onClick={() => selectRoot("firmen")}>
              <span className="root-icon">🏢</span>
              <span className="root-label">Firmen</span>
              <span className="root-path">{roots.firmen}</span>
            </button>
            <button className="root-card" onClick={() => selectRoot("objekte")}>
              <span className="root-icon">🏠</span>
              <span className="root-label">Objekte</span>
              <span className="root-path">{roots.objekte}</span>
            </button>
            <button className="root-card root-card--tool" onClick={() => navigate("/zuordnen/komprimierung")}>
              <span className="root-icon">🗜️</span>
              <span className="root-label">Zähler verkleinern</span>
              <span className="root-path">Zähler-Fotos komprimieren + Synology-Backup</span>
            </button>
            <button className="root-card root-card--tool" onClick={() => navigate("/zuordnen/bilder-komprimierung")}>
              <span className="root-icon">🖼️</span>
              <span className="root-label">Bilder verkleinern</span>
              <span className="root-path">Holthey Bilder komprimieren + Synology-Backup</span>
            </button>
          </div>
        </div>
      )}

      {/* Browser + Analyse */}
      {currentPath && (
        <>
          {/* Breadcrumbs */}
          <div className="zuordnen-breadcrumbs">
            <button className="crumb-root" onClick={() => { setCurrentPath(""); setBrowseData(null); setAnalyzeResult(null); setPreviewData(null); setEditingSchema(null); setPathHistory([]); }}>
              ⌂
            </button>
            {buildBreadcrumbs().map((c, i) => (
              <React.Fragment key={i}>
                <span className="crumb-sep">/</span>
                <button className="crumb-btn" onClick={() => { setPathHistory([]); browseTo(c.path); }}>{c.label}</button>
              </React.Fragment>
            ))}
          </div>

          {/* Toolbar */}
          <div className="zuordnen-toolbar">
            {pathHistory.length > 0 && (
              <button className="tool-btn" onClick={goUp}>⬆ Hoch</button>
            )}

            <div className="view-toggle">
              <button className={`vt-btn ${viewMode === "browse" ? "active" : ""}`} onClick={() => setViewMode("browse")}>
                Ordner
              </button>
              <button
                className={`vt-btn analyse-menu-btn ${["schema","falsch","sonstige","rgnr","analyze"].includes(viewMode) ? "active" : ""}`}
                onClick={() => setShowAnalyseMenu(true)}
              >
                🔍 Analyse ▾
              </button>
            </div>
          </div>

          {/* ═══════ RGNR MODAL ═══════ */}
          {showRgnrModal && (
            <div className="rgnr-modal-overlay" onClick={() => setShowRgnrModal(false)}>
              <div className="rgnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="rgnr-modal-header">
                  <h3>🔢 Rechnungsnummern analysieren</h3>
                  <button className="rgnr-modal-close" onClick={() => setShowRgnrModal(false)}>✕</button>
                </div>

                <div className="rgnr-modal-body">
                  {/* Format */}
                  <div className="rgnr-field-group">
                    <label className="rgnr-field-label">Format</label>
                    <div className="rgnr-radio-row">
                      <label className={`rgnr-radio ${rgnrFormat === "standard" ? "active" : ""}`}>
                        <input type="radio" checked={rgnrFormat === "standard"} onChange={() => setRgnrFormat("standard")} />
                        Standard (automatisch erkennen)
                      </label>
                      <label className={`rgnr-radio ${rgnrFormat === "custom" ? "active" : ""}`}>
                        <input type="radio" checked={rgnrFormat === "custom"} onChange={() => setRgnrFormat("custom")} />
                        Eigenes Format
                      </label>
                    </div>
                    {rgnrFormat === "custom" && (
                      <div className="rgnr-custom-pattern">
                        <input
                          type="text"
                          className="rgnr-input"
                          placeholder="z.B. RE-XXXX-0000"
                          value={rgnrCustomPattern}
                          onChange={(e) => setRgnrCustomPattern(e.target.value)}
                        />
                        <span className="rgnr-hint">X = Buchstabe &nbsp;·&nbsp; 0 = Ziffer (oder die Ziffer 0)</span>
                      </div>
                    )}
                  </div>

                  {/* Direktsuche */}
                  <div className="rgnr-field-group">
                    <label className="rgnr-field-label">Direktsuche im Dateinamen <span className="rgnr-optional">(optional)</span></label>
                    <input
                      type="text"
                      className="rgnr-input"
                      placeholder="z.B. RE-2024 oder Mustermann"
                      value={rgnrSearchText}
                      onChange={(e) => setRgnrSearchText(e.target.value)}
                    />
                  </div>

                  {/* Bereich */}
                  <div className="rgnr-field-group">
                    <label className="rgnr-field-label">Bereich</label>
                    <div className="rgnr-radio-row">
                      <label className={`rgnr-radio ${rgnrRecursive ? "active" : ""}`}>
                        <input type="radio" checked={rgnrRecursive} onChange={() => setRgnrRecursive(true)} />
                        Aktueller Ordner + alle Unterordner
                      </label>
                      <label className={`rgnr-radio ${!rgnrRecursive ? "active" : ""}`}>
                        <input type="radio" checked={!rgnrRecursive} onChange={() => setRgnrRecursive(false)} />
                        Nur aktueller Ordner
                      </label>
                    </div>
                  </div>

                  {/* Was prüfen */}
                  <div className="rgnr-field-group">
                    <label className="rgnr-field-label">Was prüfen?</label>
                    <label className="rgnr-check-label active">
                      <input type="checkbox" checked readOnly />
                      Lücken in der Zahlenreihenfolge
                    </label>
                  </div>
                </div>

                <div className="rgnr-modal-footer">
                  <button className="rgnr-cancel-btn" onClick={() => setShowRgnrModal(false)}>Abbrechen</button>
                  <button
                    className="rgnr-start-btn"
                    onClick={runRgnrAnalyze}
                    disabled={rgnrLoading || (rgnrFormat === "custom" && !rgnrCustomPattern.trim())}
                  >
                    {rgnrLoading ? "Scanne…" : "Analyse starten"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ═══════ ANALYSE AUSWAHL-POPUP ═══════ */}
          {showAnalyseMenu && (
            <div className="analyse-menu-overlay" onClick={() => setShowAnalyseMenu(false)}>
              <div className="analyse-menu" onClick={(e) => e.stopPropagation()}>
                <div className="analyse-menu-header">
                  <h3>🔍 Analyse</h3>
                  <button className="rgnr-modal-close" onClick={() => setShowAnalyseMenu(false)}>✕</button>
                </div>
                <div className="analyse-menu-tiles">
                  <button className="analyse-tile" onClick={() => { setShowAnalyseMenu(false); setShowRgnrModal(true); }}>
                    <span className="tile-icon">🔢</span>
                    <span className="tile-title">Rechnungsnummern</span>
                    <span className="tile-desc">Lücken in Nummernfolgen</span>
                  </button>
                  <button className="analyse-tile" onClick={() => { setShowAnalyseMenu(false); setShowSchemaModal(true); }}>
                    <span className="tile-icon">🏷️</span>
                    <span className="tile-title">Schema-Erkennung</span>
                    <span className="tile-desc">Prefix, Absender, # in Teilen</span>
                  </button>
                  <button className="analyse-tile" onClick={() => { setShowAnalyseMenu(false); setShowFalschModal(true); }}>
                    <span className="tile-icon">📁</span>
                    <span className="tile-title">Falsch geordnet</span>
                    <span className="tile-desc">Falscher Ordner oder Jahr</span>
                  </button>
                  <button className="analyse-tile" onClick={() => { setShowAnalyseMenu(false); setShowSonstigeModal(true); }}>
                    <span className="tile-icon">⚠️</span>
                    <span className="tile-title">Sonstige Fehler</span>
                    <span className="tile-desc">Bindestrich, Sonderzeichen, Duplikate</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ═══════ SCHEMA-MODAL ═══════ */}
          {showSchemaModal && (
            <div className="rgnr-modal-overlay" onClick={() => setShowSchemaModal(false)}>
              <div className="rgnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="rgnr-modal-header">
                  <h3>🏷️ Schema-Erkennung</h3>
                  <button className="rgnr-modal-close" onClick={() => setShowSchemaModal(false)}>✕</button>
                </div>
                <div className="rgnr-modal-body">
                  <div className="rgnr-field-group">
                    <label className="rgnr-field-label">Bereich</label>
                    <div className="rgnr-radio-row">
                      <label className={`rgnr-radio ${schemaRecursive ? "active" : ""}`}>
                        <input type="radio" checked={schemaRecursive} onChange={() => setSchemaRecursive(true)} />
                        Aktueller Ordner + Unterordner
                      </label>
                      <label className={`rgnr-radio ${!schemaRecursive ? "active" : ""}`}>
                        <input type="radio" checked={!schemaRecursive} onChange={() => setSchemaRecursive(false)} />
                        Nur aktueller Ordner
                      </label>
                    </div>
                  </div>
                  <div className="rgnr-field-group">
                    <label className="rgnr-field-label">Prüfkriterien</label>
                    {[
                      ["no_prefix", "Kein Prefix erkannt"],
                      ["unknown_prefix", "Prefix nicht in DB"],
                      ["no_absender", "Kein Absender"],
                      ["hash_in_part", "# in einem Teil (Platzhalter nicht gefüllt)"],
                      ["no_date", "Kein Datum erkannt"],
                      ["no_schema_part", "Schema-Part fehlt (< 4 Teile)"],
                      ["wrong_separator", "Bindestrich ohne Leerzeichen"],
                    ].map(([k, l]) => (
                      <label key={k} className={`rgnr-check-label ${schemaChecks.includes(k) ? "active" : ""}`}>
                        <input type="checkbox" checked={schemaChecks.includes(k)} onChange={() => toggleSchemaCheck(k)} />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="rgnr-modal-footer">
                  <button className="rgnr-cancel-btn" onClick={() => setShowSchemaModal(false)}>Abbrechen</button>
                  <button className="rgnr-start-btn" onClick={runSchemaAnalyze} disabled={schemaLoading || schemaChecks.length === 0}>
                    {schemaLoading ? "Scanne…" : "Analyse starten"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ═══════ FALSCH-MODAL ═══════ */}
          {showFalschModal && (
            <div className="rgnr-modal-overlay" onClick={() => setShowFalschModal(false)}>
              <div className="rgnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="rgnr-modal-header">
                  <h3>📁 Falsch geordnet</h3>
                  <button className="rgnr-modal-close" onClick={() => setShowFalschModal(false)}>✕</button>
                </div>
                <div className="rgnr-modal-body">
                  <div className="rgnr-field-group">
                    <label className="rgnr-field-label">Bereich</label>
                    <div className="rgnr-radio-row">
                      <label className={`rgnr-radio ${falschRecursive ? "active" : ""}`}>
                        <input type="radio" checked={falschRecursive} onChange={() => setFalschRecursive(true)} />
                        Aktueller Ordner + Unterordner
                      </label>
                      <label className={`rgnr-radio ${!falschRecursive ? "active" : ""}`}>
                        <input type="radio" checked={!falschRecursive} onChange={() => setFalschRecursive(false)} />
                        Nur aktueller Ordner
                      </label>
                    </div>
                  </div>
                  <div className="rgnr-field-group">
                    <label className="rgnr-field-label">Prüfungen</label>
                    <label className={`rgnr-check-label ${falschCheckPrefix ? "active" : ""}`}>
                      <input type="checkbox" checked={falschCheckPrefix} onChange={(e) => setFalschCheckPrefix(e.target.checked)} />
                      Prefix stimmt nicht mit Ordner überein
                    </label>
                    <label className={`rgnr-check-label ${falschCheckYear ? "active" : ""}`}>
                      <input type="checkbox" checked={falschCheckYear} onChange={(e) => setFalschCheckYear(e.target.checked)} />
                      Jahr im Dateiname passt nicht zum Jahres-Ordner
                    </label>
                  </div>
                </div>
                <div className="rgnr-modal-footer">
                  <button className="rgnr-cancel-btn" onClick={() => setShowFalschModal(false)}>Abbrechen</button>
                  <button className="rgnr-start-btn" onClick={runFalschAnalyze} disabled={falschLoading || (!falschCheckPrefix && !falschCheckYear)}>
                    {falschLoading ? "Scanne…" : "Analyse starten"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ═══════ SONSTIGE-MODAL ═══════ */}
          {showSonstigeModal && (
            <div className="rgnr-modal-overlay" onClick={() => setShowSonstigeModal(false)}>
              <div className="rgnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="rgnr-modal-header">
                  <h3>⚠️ Sonstige Fehler</h3>
                  <button className="rgnr-modal-close" onClick={() => setShowSonstigeModal(false)}>✕</button>
                </div>
                <div className="rgnr-modal-body">
                  <div className="rgnr-field-group">
                    <label className="rgnr-field-label">Bereich</label>
                    <div className="rgnr-radio-row">
                      <label className={`rgnr-radio ${sonstigeRecursive ? "active" : ""}`}>
                        <input type="radio" checked={sonstigeRecursive} onChange={() => setSonstigeRecursive(true)} />
                        Aktueller Ordner + Unterordner
                      </label>
                      <label className={`rgnr-radio ${!sonstigeRecursive ? "active" : ""}`}>
                        <input type="radio" checked={!sonstigeRecursive} onChange={() => setSonstigeRecursive(false)} />
                        Nur aktueller Ordner
                      </label>
                    </div>
                  </div>
                  <div className="rgnr-field-group">
                    <label className="rgnr-field-label">Wird automatisch geprüft</label>
                    <div className="misc-check-list">
                      <span>✓ Doppelter Bindestrich (--)</span>
                      <span>✓ Doppeltes Leerzeichen</span>
                      <span>✓ Unerlaubte Sonderzeichen</span>
                      <span>✓ Sehr kurzer Dateiname</span>
                      <span>✓ Dateiname doppelt vorhanden</span>
                      <span>✓ Doppelter Punkt</span>
                    </div>
                  </div>
                </div>
                <div className="rgnr-modal-footer">
                  <button className="rgnr-cancel-btn" onClick={() => setShowSonstigeModal(false)}>Abbrechen</button>
                  <button className="rgnr-start-btn" onClick={runSonstigeAnalyze} disabled={sonstigeLoading}>
                    {sonstigeLoading ? "Scanne…" : "Analyse starten"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Loading */}
          {(browseLoading || analyzeLoading || rgnrLoading || schemaLoading || falschLoading || sonstigeLoading) && (
            <div className="loading-overlay">
              <div className="loading-spinner" />
              <p>{browseLoading ? "Lade Ordner…" : rgnrLoading ? "Scanne Rechnungsnummern…" : "Ordner wird rekursiv gescannt…"}</p>
            </div>
          )}

          {/* ═══════ BROWSE-ANSICHT ═══════ */}
          {viewMode === "browse" && browseData && !browseLoading && (
            <div className="browse-result">
              {/* Ordner */}
              {browseData.folders.length > 0 && (
                <div className="browse-section">
                  <h4>📁 Ordner ({browseData.total_folders})</h4>
                  <div className="browse-list">
                    {browseData.folders.map((f, i) => (
                      <button key={i} className="browse-item folder" onClick={() => openFolder(f.path)}>
                        📁 {f.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Schema-Zusammenfassung */}
              {browseData.schema_counts && Object.keys(browseData.schema_counts).length > 0 && (
                <div className="schema-summary">
                  <h4>Erkannte Dokumenttypen</h4>
                  <div className="schema-chips">
                    {Object.entries(browseData.schema_counts).map(([sid, count]) => (
                      <button
                        key={sid}
                        className={`schema-chip ${editingSchema === sid ? "editing" : ""}`}
                        style={{ "--chip-color": SCHEMA_COLORS[sid] || "#888" }}
                        onClick={() => openSchemaEditor(sid)}
                      >
                        <span className="chip-dot" />
                        <span>{schemas?.[sid]?.label || sid}</span>
                        <span className="chip-count">{count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Schema-Editor */}
              {editingSchema && schemas && (
                <div className="schema-editor">
                  <div className="se-header">
                    <h4>
                      <span className="se-dot" style={{ background: SCHEMA_COLORS[editingSchema] || "#888" }} />
                      Schema: {schemas[editingSchema]?.label}
                    </h4>
                    <button className="se-close" onClick={() => { setEditingSchema(null); setPreviewData(null); }}>✕</button>
                  </div>
                  <p className="se-desc">{schemas[editingSchema]?.beschreibung}</p>
                  <div className="se-template-row">
                    <label>Template:</label>
                    <input
                      type="text"
                      className="se-input"
                      value={editTemplate}
                      onChange={(e) => setEditTemplate(e.target.value)}
                    />
                    <button className="tool-btn analyze-btn" onClick={generatePreview} disabled={previewLoading}>
                      {previewLoading ? "Lade…" : "Vorschau"}
                    </button>
                  </div>
                  <div className="se-example">
                    <span className="se-example-label">Beispiel:</span>
                    <code>{schemas[editingSchema]?.example}</code>
                  </div>

                  {/* Verfügbare Platzhalter */}
                  <div className="se-placeholders">
                    <span className="se-ph-label">Platzhalter:</span>
                    {["[PREFIX]", "[DATUM]", "[MONAT]", "[JAHR]", "[ABSENDER]", "[EMPFÄNGER]", "[RG-NR]",
                      "[VERSORGER]", "[VERTRAG]", "[MITARBEITER]", "[MANDANT]", "[VARIANTE]",
                      "[MIETER]", "[ZUSATZ]", "[BLZ]", "[KONTO]", "[NR]", "[M]"].map((ph) => (
                      <button key={ph} className="ph-tag" onClick={() => setEditTemplate((t) => t.replace(".pdf", ` - ${ph}.pdf`))}>{ph}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Rename-Vorschau */}
              {previewData && (
                <div className="preview-section">
                  <div className="preview-header">
                    <h4>Vorschau: {previewData.changed} von {previewData.total} Dateien würden umbenannt</h4>
                    {previewData.changed > 0 && (
                      <div className="preview-actions">
                        <button className="mini-btn" onClick={() => selectAll(previewData.previews.filter((p) => p.changed))}>Alle</button>
                        <button className="mini-btn" onClick={selectNone}>Keine</button>
                        <span className="sel-count">{selected.size} ausgewählt</span>
                        <button
                          className="tool-btn analyze-btn"
                          onClick={batchRenameSelected}
                          disabled={selected.size === 0 || actionLoading.batch}
                        >
                          {actionLoading.batch ? "Wird umbenannt…" : `✓ ${selected.size} umbenennen`}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="preview-list">
                    {previewData.previews.map((p, idx) => (
                      <div key={idx} className={`preview-card ${p.changed ? "has-change" : "no-change"} ${actionDone[`rename-${idx}`] ? "done" : ""}`}>
                        {p.changed && (
                          <div className="preview-check">
                            <input
                              type="checkbox"
                              checked={selected.has(p.path)}
                              onChange={() => toggleSelect(p.path)}
                              disabled={actionDone[`rename-${idx}`]}
                            />
                          </div>
                        )}
                        <div className="preview-body">
                          <div className="preview-old">
                            <span className="pv-label">Alt:</span>
                            <span className="pv-name">{p.old_name}</span>
                          </div>
                          {p.changed && (
                            <div className="preview-new">
                              <span className="pv-label">Neu:</span>
                              <span className="pv-name pv-new-name">{p.new_name}</span>
                            </div>
                          )}
                          {!p.changed && (
                            <div className="preview-ok"><span className="pv-ok-tag">✓ Passt bereits</span></div>
                          )}
                        </div>
                        {p.changed && !actionDone[`rename-${idx}`] && (
                          <button
                            className="sug-btn"
                            disabled={actionLoading[`rename-${idx}`]}
                            onClick={() => doRename(p.path, p.new_name, idx)}
                          >
                            {actionLoading[`rename-${idx}`] ? "…" : "Ja"}
                          </button>
                        )}
                        {actionDone[`rename-${idx}`] && <span className="done-badge">✓</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dateien mit Schema-Typ */}
              {!previewData && browseData.files.length > 0 && (
                <div className="browse-section">
                  <h4>
                    📄 Dateien ({browseData.total_files})
                    {browseData.total_fixes > 0 && (
                      <span className="fix-header-badge" onClick={applyAllFixes} title="Alle Fixes anwenden">
                        ⚠️ {browseData.total_fixes} Format-Fehler
                        <button className="fix-all-btn">Alle fixen</button>
                      </span>
                    )}
                  </h4>
                  <div className="browse-list">
                    {browseData.files.map((f, i) => (
                      <div key={i} className={`browse-item file-detail ${f.fixes?.length ? "has-fix" : ""}`}>
                        <div className="file-main-row">
                          <span className="file-name">📄 {f.name}</span>
                          <span
                            className="file-schema-tag"
                            style={{ "--tag-color": SCHEMA_COLORS[f.schema_id] || "#888" }}
                          >
                            {f.schema_label}
                          </span>
                          {f.fixes?.length > 0 && !actionDone[`fix-${i}`] && (
                            <span className="fix-badge">⚠️</span>
                          )}
                          {actionDone[`fix-${i}`] && <span className="done-badge">✓</span>}
                          <span className="file-size">{fmtSize(f.size)}</span>
                        </div>
                        {f.fixes?.length > 0 && !actionDone[`fix-${i}`] && (
                          <div className="fix-suggestion">
                            {f.fixes.map((fix, fi) => (
                              <div key={fi} className="fix-row">
                                <span className="fix-label">🔧 {fix.beschreibung}:</span>
                                <span className="fix-preview">{fix.fix}</span>
                                <button
                                  className="fix-btn"
                                  disabled={actionLoading[`fix-${i}`]}
                                  onClick={() => applyFix(f.path, fix.fix, i)}
                                >
                                  {actionLoading[`fix-${i}`] ? "…" : "Fixen"}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {browseData.folders.length === 0 && browseData.files.length === 0 && (
                <p className="empty-hint">Ordner ist leer</p>
              )}
            </div>
          )}

          {/* ═══════ RGNR-ERGEBNIS ═══════ */}
          {viewMode === "rgnr" && rgnrResult && !rgnrLoading && (
            <div className="rgnr-result">
              <div className="rgnr-summary">
                <span>📂 {rgnrResult.total_files_scanned} Dateien gescannt</span>
                <span>📋 {rgnrResult.total_groups} Gruppen</span>
                {rgnrResult.groups_with_gaps > 0
                  ? <span className="rgnr-badge rgnr-badge-warn">🔴 {rgnrResult.groups_with_gaps} Gruppen mit Lücken · {rgnrResult.total_gaps} fehlende Nummern</span>
                  : <span className="rgnr-badge rgnr-badge-ok">✓ Keine Lücken</span>
                }
                <button className="rgnr-rerun-btn" onClick={() => setShowRgnrModal(true)}>⚙ Neu konfigurieren</button>
              </div>

              {rgnrResult.groups.length === 0 && (
                <p className="empty-hint">Keine Rechnungsnummern gefunden. Evtl. anderes Format oder Suchtext angeben.</p>
              )}

              <div className="rgnr-groups">
                {rgnrResult.groups.map((g, gi) => (
                  <div key={gi} className={`rgnr-group ${g.has_gaps ? "has-gaps" : "no-gaps"}`}>
                    <div className="rgnr-group-header" onClick={() => setRgnrExpandedGroup(rgnrExpandedGroup === gi ? null : gi)}>
                      <div className="rgnr-group-title">
                        <span className="rgnr-prefix">{g.group || "—"}</span>
                      </div>
                      <div className="rgnr-group-meta">
                        <span className="rgnr-range">{g.existing_range}</span>
                        <span className="rgnr-count">{g.total} vorhanden</span>
                        {g.has_gaps
                          ? <span className="rgnr-gap-badge">🔴 {g.gaps.length} fehlend</span>
                          : <span className="rgnr-ok-badge">✓</span>
                        }
                        <span className="rgnr-expand-icon">{rgnrExpandedGroup === gi ? "▲" : "▼"}</span>
                      </div>
                    </div>

                    {g.has_gaps && (
                      <div className="rgnr-gaps-row">
                        <span className="rgnr-gaps-label">Fehlende Nummern:</span>
                        <div className="rgnr-gaps-list">
                          {g.gaps.map((n, ni) => <span key={ni} className="rgnr-gap-num">{n}</span>)}
                        </div>
                      </div>
                    )}

                    {rgnrExpandedGroup === gi && (
                      <div className="rgnr-items-list">
                        {g.items.map((item, ii) => (
                          <div key={ii} className="rgnr-item">
                            <span className="rgnr-item-num">#{item.num}</span>
                            <span className="rgnr-item-file">{item.filename}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══════ ANALYSE-ANSICHT ═══════ */}
          {viewMode === "analyze" && analyzeResult && !analyzeLoading && (
            <div className="analyze-result">
              <div className="analyze-summary">
                <span>📊 {analyzeResult.total_files} Dateien in {analyzeResult.total_folders} Ordnern</span>
                {analyzeResult.wrong_folder_count > 0 && <span className="badge badge-warn">{analyzeResult.wrong_folder_count} falsch</span>}
                {analyzeResult.schema_errors_count > 0 && <span className="badge badge-error">{analyzeResult.schema_errors_count} Schema</span>}
                {analyzeResult.missing_invoices_count > 0 && <span className="badge badge-info">{analyzeResult.missing_invoices_count} Lücken</span>}
                {(analyzeResult.wrong_folder_count || 0) === 0 && (analyzeResult.schema_errors_count || 0) === 0 && (analyzeResult.missing_invoices_count || 0) === 0 && (
                  <span className="badge badge-ok">✓ Alles OK</span>
                )}
              </div>

              <div className="analyze-tabs">
                {analyzeResult.wrong_folder && <button className={`tab-btn ${activeTab === "wrong_folder" ? "active" : ""}`} onClick={() => setActiveTab("wrong_folder")}>Falscher Ordner ({analyzeResult.wrong_folder_count})</button>}
                {analyzeResult.schema_errors && <button className={`tab-btn ${activeTab === "schema" ? "active" : ""}`} onClick={() => setActiveTab("schema")}>Schema ({analyzeResult.schema_errors_count})</button>}
                {analyzeResult.missing_invoices && <button className={`tab-btn ${activeTab === "missing_invoice" ? "active" : ""}`} onClick={() => setActiveTab("missing_invoice")}>Rg-Lücken ({analyzeResult.missing_invoices_count})</button>}
              </div>

              {/* Falscher Ordner */}
              {activeTab === "wrong_folder" && analyzeResult.wrong_folder && (
                <div className="tab-content">
                  {analyzeResult.wrong_folder.length === 0 ? (
                    <p className="empty-hint">✓ Keine falsch zugeordneten Dateien</p>
                  ) : (
                    <div className="issue-list">
                      {analyzeResult.wrong_folder.map((item, idx) => (
                        <div key={idx} className={`issue-card ${actionDone[`move-${idx}`] ? "done" : ""}`}>
                          <div className="issue-body">
                            <div className="issue-file">{item.file}</div>
                            <div className="issue-detail">
                              <span className="issue-prefix">{item.prefix}</span>
                              <span className="issue-pfad">{item.current_folder}</span>
                            </div>
                            {item.suggestions.length > 0 && (
                              <div className="suggestions">
                                <span className="sug-label">→</span>
                                {item.suggestions.map((s, si) => (
                                  <button key={si} className="sug-btn" disabled={actionLoading[`move-${idx}`] || actionDone[`move-${idx}`]}
                                    onClick={() => { if (window.confirm(`Verschieben nach:\n${s.base_path}/${item.file}`)) moveFile(item.path, `${s.base_path}/${item.file}`, idx); }}>
                                    {s.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          {actionDone[`move-${idx}`] && <span className="done-badge">✓</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Schema-Fehler */}
              {activeTab === "schema" && analyzeResult.schema_errors && (
                <div className="tab-content">
                  {analyzeResult.schema_errors.length === 0 ? (
                    <p className="empty-hint">✓ Keine Schema-Fehler</p>
                  ) : (
                    <div className="issue-list">
                      {analyzeResult.schema_errors.map((item, idx) => (
                        <div key={idx} className="issue-card schema-card">
                          <div className="issue-body">
                            <div className="issue-file">{item.file}</div>
                            <div className="issue-detail"><span className="issue-pfad">{item.path}</span></div>
                            <div className="schema-errors">
                              {item.errors.map((err, ei) => <span key={ei} className="schema-err-tag">⚠ {err}</span>)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Rg-Lücken */}
              {activeTab === "missing_invoice" && analyzeResult.missing_invoices && (
                <div className="tab-content">
                  {analyzeResult.missing_invoices.length === 0 ? (
                    <p className="empty-hint">✓ Keine Lücken</p>
                  ) : (
                    <div className="issue-list">
                      {analyzeResult.missing_invoices.map((item, idx) => (
                        <div key={idx} className="issue-card invoice-card">
                          <div className="issue-body">
                            <div className="issue-file">{item.prefix} / {item.sender}</div>
                            <div className="issue-detail">
                              <span>Bereich: {item.existing_range}</span>
                              <span>{item.total_existing} vorhanden</span>
                            </div>
                            <div className="missing-nums">
                              <span className="sug-label">Fehlend:</span>
                              {item.missing_numbers.map((n, ni) => <span key={ni} className="missing-num">{n}</span>)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ═══════ SCHEMA-ERGEBNIS ═══════ */}
          {viewMode === "schema" && schemaResult && !schemaLoading && (
            <div className="analyse-result-view">
              <div className="analyse-result-header">
                <div className="analyse-result-summary">
                  <span>🏷️ {schemaResult.total_scanned} PDFs gescannt</span>
                  {schemaResult.total_errors > 0
                    ? <span className="badge badge-error">⚠️ {schemaResult.total_errors} mit Fehlern</span>
                    : <span className="badge badge-ok">✓ Keine Fehler</span>
                  }
                  <span className="badge badge-ok-soft">✓ {schemaResult.ok_count} korrekt</span>
                </div>
                <button className="rgnr-rerun-btn" onClick={() => setShowSchemaModal(true)}>⚙ Neu konfigurieren</button>
              </div>

              {schemaResult.total_errors === 0 && (
                <p className="empty-hint">✓ Alle PDFs haben ein korrektes Schema.</p>
              )}

              <div className="issue-list">
                {schemaResult.files.map((item, idx) => (
                  <div key={idx} className="issue-card schema-card">
                    <div className="issue-body" onClick={() => setSchemaExpandedFile(schemaExpandedFile === idx ? null : idx)} style={{cursor:"pointer"}}>
                      <div className="issue-file">📄 {item.file}</div>
                      <div className="schema-errors">
                        {item.errors.map((err, ei) => (
                          <span key={ei} className={`schema-err-tag err-${err.type}`}>⚠ {err.label}</span>
                        ))}
                      </div>
                    </div>
                    {schemaExpandedFile === idx && (
                      <div className="issue-detail">
                        <span className="issue-pfad">{item.path}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══════ FALSCH-GEORDNET-ERGEBNIS ═══════ */}
          {viewMode === "falsch" && falschResult && !falschLoading && (
            <div className="analyse-result-view">
              <div className="analyse-result-header">
                <div className="analyse-result-summary">
                  <span>📁 {falschResult.total_files} Dateien gescannt</span>
                  {(falschResult.wrong_folder_count || 0) > 0 && <span className="badge badge-warn">{falschResult.wrong_folder_count} falscher Ordner</span>}
                  {(falschResult.wrong_year_count || 0) > 0 && <span className="badge badge-warn">{falschResult.wrong_year_count} falsches Jahr</span>}
                  {(falschResult.wrong_folder_count || 0) === 0 && (falschResult.wrong_year_count || 0) === 0 && (
                    <span className="badge badge-ok">✓ Alles korrekt geordnet</span>
                  )}
                </div>
                <button className="rgnr-rerun-btn" onClick={() => setShowFalschModal(true)}>⚙ Neu konfigurieren</button>
              </div>

              {/* Falscher Ordner */}
              {falschResult.wrong_folder && falschResult.wrong_folder.length > 0 && (
                <div className="falsch-section">
                  <h4 className="falsch-section-title">Prefix passt nicht zum Ordner ({falschResult.wrong_folder_count})</h4>
                  <div className="issue-list">
                    {falschResult.wrong_folder.map((item, idx) => (
                      <div key={idx} className={`issue-card ${actionDone[`move-${idx}`] ? "done" : ""}`}>
                        <div className="issue-body">
                          <div className="issue-file">{item.file}</div>
                          <div className="issue-detail">
                            <span className="issue-prefix">{item.prefix}</span>
                            <span className="issue-pfad">{item.current_folder}</span>
                          </div>
                          {item.suggestions.length > 0 && (
                            <div className="suggestions">
                              <span className="sug-label">→</span>
                              {item.suggestions.map((s, si) => (
                                <button key={si} className="sug-btn"
                                  disabled={actionLoading[`move-${idx}`] || actionDone[`move-${idx}`]}
                                  onClick={() => { if (window.confirm(`Verschieben nach:\n${s.base_path}/${item.file}`)) moveFile(item.path, `${s.base_path}/${item.file}`, idx); }}>
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {actionDone[`move-${idx}`] && <span className="done-badge">✓</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Falsches Jahr */}
              {falschResult.wrong_year && falschResult.wrong_year.length > 0 && (
                <div className="falsch-section">
                  <h4 className="falsch-section-title">Jahr passt nicht zum Jahres-Ordner ({falschResult.wrong_year_count})</h4>
                  <div className="issue-list">
                    {falschResult.wrong_year.map((item, idx) => (
                      <div key={idx} className="issue-card">
                        <div className="issue-body">
                          <div className="issue-file">{item.file}</div>
                          <div className="issue-detail">
                            <span className="badge badge-error">Datei: {item.file_year}</span>
                            <span className="badge badge-warn">Ordner: {item.folder_year}</span>
                            <span className="issue-pfad">{item.current_folder}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(falschResult.wrong_folder_count || 0) === 0 && (falschResult.wrong_year_count || 0) === 0 && (
                <p className="empty-hint">✓ Alle Dateien sind korrekt geordnet.</p>
              )}
            </div>
          )}

          {/* ═══════ SONSTIGE-FEHLER-ERGEBNIS ═══════ */}
          {viewMode === "sonstige" && sonstigeResult && !sonstigeLoading && (
            <div className="analyse-result-view">
              <div className="analyse-result-header">
                <div className="analyse-result-summary">
                  <span>⚠️ {sonstigeResult.total_scanned} Dateien gescannt</span>
                  {sonstigeResult.total_errors > 0
                    ? <span className="badge badge-error">{sonstigeResult.total_errors} mit Fehlern</span>
                    : <span className="badge badge-ok">✓ Keine Fehler</span>
                  }
                  <span className="badge badge-ok-soft">✓ {sonstigeResult.ok_count} korrekt</span>
                </div>
                <button className="rgnr-rerun-btn" onClick={() => setShowSonstigeModal(true)}>⚙ Neu konfigurieren</button>
              </div>

              {sonstigeResult.total_errors === 0 && (
                <p className="empty-hint">✓ Keine sonstigen Fehler gefunden.</p>
              )}

              <div className="issue-list">
                {sonstigeResult.files.map((item, idx) => (
                  <div key={idx} className="issue-card">
                    <div className="issue-body">
                      <div className="issue-file">📄 {item.file}</div>
                      <div className="misc-errors">
                        {item.issues.map((iss, ii) => (
                          <span key={ii} className={`schema-err-tag err-${iss.type}`}>⚠ {iss.label}</span>
                        ))}
                      </div>
                      <div className="issue-detail">
                        <span className="issue-pfad">{item.path}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );
}

export default ZuordnenPage;
